import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Zap, TrendingUp, CheckCircle2, Rocket, Target, Users } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const metrics = [
  {
    icon: Clock,
    value: '3-8 Weeks',
    label: 'Average Implementation',
    description: 'From kickoff to production deployment'
  },
  {
    icon: Zap,
    value: '60-80%',
    label: 'Faster Than Traditional',
    description: 'Compared to big consulting firms'
  },
  {
    icon: Rocket,
    value: '200+',
    label: 'Rapid Deployments',
    description: 'Successful implementations delivered'
  },
  {
    icon: Target,
    value: '95%',
    label: 'On-Time Delivery',
    description: 'Projects delivered within agreed timelines'
  }
];

const comparisons = [
  {
    phase: 'Discovery & Planning',
    traditional: '4-8 weeks',
    agility: '3-5 days',
    highlight: true
  },
  {
    phase: 'Architecture & Design',
    traditional: '6-12 weeks',
    agility: '1-2 weeks',
    highlight: false
  },
  {
    phase: 'Development & Testing',
    traditional: '12-24 weeks',
    agility: '2-6 weeks',
    highlight: true
  },
  {
    phase: 'Deployment & Training',
    traditional: '4-8 weeks',
    agility: '3-7 days',
    highlight: false
  },
  {
    phase: 'Total Timeline',
    traditional: '6-12 months',
    agility: '3-8 weeks',
    highlight: true
  }
];

const caseStudies = [
  {
    industry: 'Insurance',
    title: 'Automated Statement Processing',
    timeline: '3 weeks to production',
    results: [
      '500+ daily statements automated',
      '95% reduction in manual work',
      'Real-time CRM integration'
    ],
    link: '/case-studies/insurance-automation'
  },
  {
    industry: 'Retail',
    title: 'Power BI Analytics Platform',
    timeline: '4 weeks to deployment',
    results: [
      '60% reduction in manual reporting',
      'Real-time visibility across branches',
      'Multi-location data integration'
    ],
    link: '/case-studies/retail-analytics'
  },
  {
    industry: 'Logistics',
    title: 'Payment Automation System',
    timeline: '2.5 weeks to live',
    results: [
      '1000+ delivery partners automated',
      '3 days to 30 minutes processing',
      'Zero Excel dependency'
    ],
    link: '/case-studies/payment-automation'
  }
];

const methodology = [
  {
    step: 1,
    title: 'Rapid Discovery',
    duration: '3-5 days',
    description: 'Direct engagement with your team to map workflows, identify automation opportunities, and define clear success metrics.',
    deliverable: 'Technical specification & implementation roadmap'
  },
  {
    step: 2,
    title: 'Agile Sprint Planning',
    duration: '1-2 days',
    description: 'Break down implementation into 1-week sprints with defined deliverables. Set up development environment and CI/CD pipelines.',
    deliverable: 'Sprint plan & development infrastructure'
  },
  {
    step: 3,
    title: 'Rapid Development',
    duration: '2-6 weeks',
    description: 'Senior engineers ship production-ready code weekly. Continuous integration, automated testing, and real-time client feedback loops.',
    deliverable: 'Working software delivered every week'
  },
  {
    step: 4,
    title: 'Deploy & Optimize',
    duration: '3-7 days',
    description: 'Production deployment with zero downtime. Team training, documentation, and performance optimization for scale.',
    deliverable: 'Live system with 24/7 monitoring'
  }
];

const differentiators = [
  {
    icon: Users,
    title: 'Senior Teams Only',
    description: 'No junior developers. Direct access to architects and senior engineers from day one.'
  },
  {
    icon: Zap,
    title: 'No Bureaucracy',
    description: 'Zero layers of account managers. Your team works directly with our engineers.'
  },
  {
    icon: Rocket,
    title: 'Weekly Deliverables',
    description: 'Working software every week. Not presentations actual production-ready code.'
  },
  {
    icon: Target,
    title: 'Fixed Timelines',
    description: 'Aggressive deadlines with accountability. We commit to timelines and deliver.'
  }
];

const SpeedImplementation = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "AI Implementation in Weeks, Not Months",
    "description": "Rapid AI implementation and deployment. 60-80% faster than traditional consulting firms with senior engineering teams and agile methodologies.",
    "url": "https://www.agilitytech.ai/speed",
    "mainEntity": {
      "@type": "Service",
      "name": "Rapid AI Implementation",
      "provider": {
        "@type": "Organization",
        "name": "Agility"
      },
      "description": "Fast-track AI implementation services delivering production-ready solutions in 3-8 weeks."
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24">
      <SEOHead
        title="AI Implementation in Weeks, Not Months | Agility"
        description="Rapid AI implementation and deployment. 60-80% faster than traditional consulting firms. Senior engineering teams delivering production-ready solutions in 3-8 weeks across automation, AI/ML, and data engineering."
        keywords="rapid AI implementation, fast AI deployment, AI implementors, quick AI integration, agile AI development, accelerated AI delivery, AI implementation speed, fast track AI deployment"
        url="https://www.agilitytech.ai/speed"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-teal-500/20 border border-teal-400 rounded-full">
              <span className="text-teal-400 font-semibold text-sm">RAPID IMPLEMENTATION</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-teal-400 bg-clip-text text-transparent">
              AI Implementation in Weeks, Not Months
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Senior engineering teams shipping production-ready AI solutions 60-80% faster than traditional consulting firms. No bureaucracy. Just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors text-lg"
              >
                Start Implementation
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/case-studies"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-teal-400 text-teal-400 hover:bg-teal-400/10 rounded-lg font-semibold transition-colors text-lg"
              >
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-black border-y border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="h-12 w-12 text-teal-400 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-lg font-semibold text-teal-400 mb-2">{metric.label}</div>
                  <div className="text-sm text-gray-400">{metric.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Comparison */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-4">Traditional vs Agile Implementation</h2>
            <p className="text-xl text-gray-400 text-center mb-12">
              See why enterprises choose our rapid deployment approach
            </p>
            <div className="bg-black rounded-xl border border-gray-800 overflow-hidden">
              <div className="grid grid-cols-3 bg-gray-800/50 p-4 font-semibold">
                <div className="text-gray-300">Phase</div>
                <div className="text-gray-300 text-center">Traditional Approach</div>
                <div className="text-teal-400 text-center">Agility Approach</div>
              </div>
              {comparisons.map((row, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-3 p-4 border-t border-gray-800 ${
                    row.highlight ? 'bg-teal-500/5' : ''
                  }`}
                >
                  <div className="font-medium text-white">{row.phase}</div>
                  <div className="text-gray-400 text-center">{row.traditional}</div>
                  <div className="text-teal-400 text-center font-semibold">{row.agility}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real Case Studies */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Proven Speed. Real Results.</h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Production deployments from actual client projects
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Link
                key={index}
                to={study.link}
                className="bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-400 transition-colors"
              >
                <div className="text-teal-400 text-sm font-semibold mb-2">{study.industry}</div>
                <h3 className="text-2xl font-bold mb-4">{study.title}</h3>
                <div className="text-3xl font-bold text-teal-400 mb-6">{study.timeline}</div>
                <ul className="space-y-3">
                  {study.results.map((result, idx) => (
                    <li key={idx} className="flex items-start text-gray-300">
                      <CheckCircle2 className="h-5 w-5 text-teal-400 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{result}</span>
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Our Rapid Implementation Process</h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Agile sprints, continuous delivery, and zero bureaucracy
          </p>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {methodology.map((phase) => (
              <div key={phase.step} className="bg-black rounded-xl p-8 border border-gray-800">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    {phase.step}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{phase.title}</h3>
                    <div className="text-teal-400 text-sm font-semibold">{phase.duration}</div>
                  </div>
                </div>
                <p className="text-gray-300 mb-4">{phase.description}</p>
                <div className="text-sm text-gray-400 border-t border-gray-800 pt-4">
                  <span className="font-semibold text-teal-400">Deliverable:</span> {phase.deliverable}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Why We Ship Faster</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {differentiators.map((diff, index) => {
              const Icon = diff.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="h-12 w-12 text-teal-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">{diff.title}</h3>
                  <p className="text-gray-400">{diff.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-900 to-cyan-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Ship Faster?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Get a detailed implementation timeline for your project. No sales calls. Just straight talk with senior engineers.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-teal-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg"
          >
            Discuss Your Project
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SpeedImplementation;
